// SAB-566 Fix 3: reusable Persona KYC launch, extracted so the account-level
// deposit screen (WalletDeposit) can launch the SAME verification flow the
// league-funds surface (NFLFundsSection) uses, instead of dead-ending on a
// "Verification required" toast.
//
// Contract preserved from NFLFundsSection:
//  - referenceId MUST equal user.id (the webhook keys verification updates off it).
//  - The Persona SDK completion status NEVER releases money. The server gate
//    (check_kyc_gate) is authoritative - always recheck it (with retry, since the
//    webhook lands asynchronously) before advancing a money action.
//
// NFLFundsSection keeps its own in-component copy (money-critical, untouched);
// this module is a parallel, standalone implementation for the new surface.

import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

// SAB-573: env-driven so PROD uses production templates/environment while
// previews/local stay on SANDBOX automatically. The literals below are the
// SANDBOX fallbacks (no prod creds needed for dev). PROD supplies the VITE_
// vars at build time on the Vercel Production scope:
//   VITE_PERSONA_TIER1_TEMPLATE_ID, VITE_PERSONA_TIER2_TEMPLATE_ID,
//   VITE_PERSONA_ENVIRONMENT_ID.
// Single source of truth - NFLFundsSection imports these (no duplicate literals).
// SAB-628: sanitize the ids at the boundary. A Vercel env value pasted with a
// trailing newline or duplicated across lines (exactly what corrupted
// VITE_PERSONA_TIER2_TEMPLATE_ID: "itmpl_x\nitmpl_x\n...") otherwise rides straight
// into the hosted-flow URL as %0A and Persona 404s the template. Take the first
// whitespace-delimited token; fall back to the sandbox literal when unset/blank.
function cleanPersonaId(raw: string | undefined, fallback: string): string {
  const token = (raw ?? '').trim().split(/\s+/)[0] || '';
  return token || fallback;
}
export const PERSONA_TIER1_TEMPLATE_ID = cleanPersonaId(
  import.meta.env.VITE_PERSONA_TIER1_TEMPLATE_ID as string | undefined,
  'itmpl_AVqbwbXjJYP1JABuvhZJu8Co1zSygR',
);
export const PERSONA_TIER2_TEMPLATE_ID = cleanPersonaId(
  import.meta.env.VITE_PERSONA_TIER2_TEMPLATE_ID as string | undefined,
  'itmpl_AVqbwbXJhrqdNUAUsHuE5qqaWQeSS6',
);
export const PERSONA_ENVIRONMENT_ID = cleanPersonaId(
  import.meta.env.VITE_PERSONA_ENVIRONMENT_ID as string | undefined,
  'env_AVqbwbXkQThgE5HqYdTVZdPJ2P8633',
);
const PERSONA_SDK_SRC = 'https://cdn.withpersona.com/dist/persona-v5.5.0.js';
const PERSONA_SDK_INTEGRITY =
  'sha384-UK+a2yEU9KOzEmsgI4IlkrXWE4AekM/iAgWF60Zuyule702g7qaQ2nYccO3tnT0A';

// Module-scoped cache: a re-mount must not re-inject the script tag.
let personaSdkPromise: Promise<void> | null = null;

export function loadPersonaSdk(): Promise<void> {
  if ((window as any).Persona) return Promise.resolve();
  if (personaSdkPromise) return personaSdkPromise;
  personaSdkPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PERSONA_SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => { personaSdkPromise = null; reject(new Error('Persona SDK failed to load')); });
      return;
    }
    const script = document.createElement('script');
    script.src = PERSONA_SDK_SRC;
    script.integrity = PERSONA_SDK_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { personaSdkPromise = null; reject(new Error('Persona SDK failed to load')); };
    document.head.appendChild(script);
  });
  return personaSdkPromise;
}

export interface LaunchPersonaOpts {
  tier: 1 | 2;
  userId: string;
  onComplete: () => void | Promise<void>;
  onCancel?: () => void;
  onError?: (err: unknown) => void;
}

// Loads the SDK and opens the inquiry. Throws if the SDK/client can't initialize
// (caller shows a "verification unavailable" message).
export async function launchPersonaInquiry({ tier, userId, onComplete, onCancel, onError }: LaunchPersonaOpts): Promise<void> {
  await loadPersonaSdk();
  const Persona = (window as any).Persona;
  if (!Persona?.Client) throw new Error('Persona client did not initialize');
  const templateId = tier === 2 ? PERSONA_TIER2_TEMPLATE_ID : PERSONA_TIER1_TEMPLATE_ID;
  const client = new Persona.Client({
    templateId,
    environmentId: PERSONA_ENVIRONMENT_ID,
    referenceId: userId,
    onReady: () => client.open(),
    onComplete: () => { void onComplete(); },
    onCancel: () => { onCancel?.(); },
    onError: (err: unknown) => { onError?.(err); },
  });
}

// Server-authoritative recheck. Polls check_kyc_gate up to `attempts` times
// (the webhook updates kyc asynchronously after the inquiry completes). Returns
// true ONLY when the gate allows - the SDK completion status is never trusted.
export async function recheckKycGate(userId: string, tier: 1 | 2, attempts = 5, delayMs = 1500): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, delayMs));
    try {
      const { data: gate, error } = await (supabase as any).rpc('check_kyc_gate', { p_user_id: userId, p_required_tier: tier });
      if (!error && gate?.allowed === true) return true;
    } catch { /* retry; webhook may not have landed yet */ }
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// HOSTED REDIRECT flow (SAB-626, porting the never-merged #118 pattern).
// Persona serves inquiry.withpersona.com with X-Frame-Options: sameorigin, so the
// embedded persona-js iframe (Client.open) is refused by the browser. The hosted
// flow navigates the top window (web) or opens the external browser (native) to
// inquiry.withpersona.com/verify and returns via redirect-uri to /persona-return
// (web) or com.sabotagefantasy.app://persona-return (native) - no frame, so the
// X-Frame-Options refusal is moot. Contract preserved: reference-id === user.id
// (persona-webhook keys kyc off it); the redirect status NEVER releases money -
// the return handler polls check_kyc_gate (recheckKycGate) before advancing.
//
// Native custom scheme (matches capacitor.config appId + the existing
// ://paypal-return / ://auth-callback deep links). Must be added to Persona →
// Allowed URI schemes, or Persona rejects it as a redirect target.
const NATIVE_REDIRECT_URI = 'com.sabotagefantasy.app://persona-return';

// Pending-KYC return context. Survives the native external-browser round trip (the
// app re-opens fresh, so an in-memory return path is lost; localStorage is not).
// 1h TTL so an abandoned inquiry self-expires. Cleared by the return handler.
const PENDING_KEY = 'sf_pending_kyc';
const PENDING_TTL_MS = 60 * 60 * 1000;

export interface PendingKyc {
  returnPath: string;
  tier: 1 | 2;
  userId: string;
  ts: number;
}

export function setPendingKyc(p: Omit<PendingKyc, 'ts'>): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ ...p, ts: Date.now() }));
  } catch {
    /* storage unavailable - web still carries the redirect via the URL */
  }
}

export function getPendingKyc(): PendingKyc | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingKyc;
    if (!p || typeof p.returnPath !== 'string' || (p.tier !== 1 && p.tier !== 2) ||
        typeof p.userId !== 'string' || typeof p.ts !== 'number') {
      clearPendingKyc();
      return null;
    }
    if (Date.now() - p.ts > PENDING_TTL_MS) {
      clearPendingKyc();
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

export function clearPendingKyc(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

// Persona hosted-flow URL. Params per Persona docs: /verify with
// inquiry-template-id, environment-id, reference-id (= userId), redirect-uri.
export function buildPersonaHostedUrl(
  { tier, userId, redirectUri }: { tier: 1 | 2; userId: string; redirectUri: string },
): string {
  const templateId = tier === 2 ? PERSONA_TIER2_TEMPLATE_ID : PERSONA_TIER1_TEMPLATE_ID;
  // Validate before building: a malformed id (embedded whitespace/newline, wrong
  // prefix) must fail loud here, never percent-encode into the URL and 404 at Persona.
  if (!/^itmpl_[A-Za-z0-9]+$/.test(templateId)) {
    throw new Error(`Persona tier-${tier} template id is malformed: ${JSON.stringify(templateId)}`);
  }
  if (!/^env_[A-Za-z0-9]+$/.test(PERSONA_ENVIRONMENT_ID)) {
    throw new Error(`Persona environment id is malformed: ${JSON.stringify(PERSONA_ENVIRONMENT_ID)}`);
  }
  const q = new URLSearchParams({
    'inquiry-template-id': templateId,
    'environment-id': PERSONA_ENVIRONMENT_ID,
    'reference-id': userId,
    'redirect-uri': redirectUri,
  });
  return `https://inquiry.withpersona.com/verify?${q.toString()}`;
}

export interface LaunchPersonaHostedOpts {
  tier: 1 | 2;
  userId: string;
  // Where to return after the inquiry completes. Defaults to the current location.
  returnPath?: string;
}

// Launch the hosted inquiry. Stashes the return context, then leaves the app: web
// navigates the top window to Persona (returns to /persona-return); native opens
// the external browser (returns via the custom scheme → NativeUrlHandler). Does
// NOT resolve to a "complete" - the caller navigates away.
export async function launchPersonaHosted({ tier, userId, returnPath }: LaunchPersonaHostedOpts): Promise<void> {
  const rp = returnPath ?? (window.location.pathname + window.location.search);
  setPendingKyc({ returnPath: rp, tier, userId });

  if (Capacitor.isNativePlatform()) {
    const url = buildPersonaHostedUrl({ tier, userId, redirectUri: NATIVE_REDIRECT_URI });
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
    return;
  }

  const url = buildPersonaHostedUrl({ tier, userId, redirectUri: `${window.location.origin}/persona-return` });
  window.location.href = url;
}
